/**
 * PlayEditorBlockContent
 *
 * Block-based play editor content that uses usePlayBlocks hook
 * instead of the legacy PlayContext.
 *
 * Phase 6: Page Integration
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EditorPageLayout } from '@/components/layout/EditorPageLayout'
import { Toolbar } from '@/components/toolbar'
import { BlockAwarePlayCanvas } from '@/components/canvas'
import { EditorCardsPanel, PlayHeader } from '@/components/plays'
import { Loading } from '@/components/ui/Loading'
import { ConceptDialog } from '@/components/concepts/ConceptDialog'
import { SelectionOverlay } from '@/components/canvas/SelectionOverlay'
import { InPlaceAnimationOverlay } from '@/components/animation/InPlaceAnimationOverlay'
import { AppliedChips } from '@/components/play-editor/AppliedChips'
import { CanvasViewportProvider } from '@/contexts/CanvasViewportContext'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { usePlayBlocks, getResolvedBlockQueryKey } from '@/hooks/usePlayBlocks'
import { useSaveStatus } from '@/contexts/SaveStatusContext'
import { useSessionSnapshot } from '@/hooks/usePlayBlocks/UseSessionSnapshot'
import { useUndo } from '@/hooks/usePlayBlocks/undo'
import { POSITION_DEBOUNCE_MS } from '@/constants/EditorConstants'
import { useConceptData } from '@/hooks/UseConceptData'
import { useKeyboardShortcuts } from '@/hooks'
import { usePlaybookData } from '@/hooks/UsePlaybookData'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { DiagramComposer, eventBus } from '@/services'
import { PlayCallParser } from '@/services/PlayCallParser'
import { useVocabularyData } from '@/hooks/UseVocabularyData'
import type { Drawing } from '@/types'
import type { VocabularyEntry } from '@/db/Types'
import type { PlayCallChip } from '@/types/BlockProperties'
import type { Concept } from '@/types/Concept.types'
import type { Player } from '@/types/Player.types'
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '@/constants/Concept.constants'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { playbookKeys } from '@/api/queries/PlaybookQueries'
import { useEntityViewTracking } from '@/hooks/UseActivityTracking'
import { secureFetch } from '@/utils/SecureFetch'
import './play-editor-page.css'

interface PlayEditorBlockContentProps {
	playbookId: string
	playBlockId: string
}

/**
 * Block-based play editor content component.
 * Uses usePlayBlocks for state management instead of PlayContext.
 */
export function PlayEditorBlockContent({
	playbookId,
	playBlockId,
}: PlayEditorBlockContentProps) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	// Track play view duration
	useEntityViewTracking('play', playBlockId)

	// Get undo/redo from context
	const {
		canUndo,
		canRedo,
		undo,
		redo,
		startTransaction,
		endTransaction,
	} = useUndo()

	// Save status tracking
	const { status: saveStatus, trackMutation } = useSaveStatus()

	// Core block-based state with mutations
	const {
		isLoading,
		error,
		resolvedBlock,
		players,
		drawings,
		drawingState,
		setDrawingState,
		hashAlignment,
		setHashAlignment,
		appliedFormation,
		appliedConcepts,
		refetch,
		mutations,
	} = usePlayBlocks({
		playBlockId,
		playbookId,
	})

	// Session snapshot for revert
	const { hasSnapshot, revertToSnapshot } = useSessionSnapshot({
		playBlockId,
		resolvedBlock,
		mutations,
		players,
		drawings,
		startTransaction,
		endTransaction,
	})

	// DiagramComposer for vocabulary-driven play building
	const composerRef = useRef(new DiagramComposer())

	// ========== Chip-based play name persistence ==========

	// Vocabulary data for legacy re-parse
	const { vocabulary } = useVocabularyData(playbookId)
	const parserRef = useRef(new PlayCallParser())

	// Extract current callChips from resolved block
	const currentCallChips = useMemo(() => {
		if (!resolvedBlock || resolvedBlock.block.type !== 'play') return undefined
		const props = resolvedBlock.block.properties as { callChips?: PlayCallChip[]; name?: string }

		// If callChips exist, use them directly
		if (props.callChips && props.callChips.length > 0) return props.callChips

		// Legacy re-parse: if no callChips but has name, parse name into chips
		if (props.name && vocabulary.length > 0) {
			const result = parserRef.current.parse(props.name, vocabulary)
			return result.tokens.map((token, i) => ({
				text: token.text,
				vocabulary_id: token.isResolved ? (token.vocabularyEntry?.id ?? null) : null,
				category: token.isResolved ? token.category : null,
				order: i,
			}))
		}

		return undefined
	}, [resolvedBlock, vocabulary])

	// Debounced chip save handler
	const chipSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleChipsChange = useCallback((chips: PlayCallChip[]) => {
		if (chipSaveTimerRef.current) {
			clearTimeout(chipSaveTimerRef.current)
		}
		chipSaveTimerRef.current = setTimeout(async () => {
			await trackMutation(() => mutations.updateCallChips(chips))
			queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		}, 400)
	}, [mutations, trackMutation, queryClient, playBlockId])

	// Clean up chip save timer on unmount and play change
	useEffect(() => {
		return () => {
			if (chipSaveTimerRef.current) {
				clearTimeout(chipSaveTimerRef.current)
			}
		}
	}, [playBlockId])

	// Debounced player position updates
	const pendingMovesRef = useRef<Map<string, { x: number; y: number }>>(new Map())
	const pendingControlPointMovesRef = useRef<Map<string, { drawingId: string; x: number; y: number }>>(new Map())
	const moveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Refs for stable callback identity in handlePlayerMove
	const drawingsRef = useRef(drawings)
	drawingsRef.current = drawings
	const playersRef = useRef(players)
	playersRef.current = players

	const flushPendingMoves = useCallback(async () => {
		const pendingPlayers = new Map(pendingMovesRef.current)
		const pendingPoints = new Map(pendingControlPointMovesRef.current)
		pendingMovesRef.current.clear()
		pendingControlPointMovesRef.current.clear()

		if (pendingPlayers.size === 0 && pendingPoints.size === 0) return

		// Flush control point and player updates in parallel
		await Promise.all([
			...Array.from(pendingPoints.entries()).map(([pointId, { drawingId, x, y }]) =>
				trackMutation(() => mutations.updateControlPoint(drawingId, pointId, x, y))
			),
			...Array.from(pendingPlayers.entries()).map(([id, pos]) =>
				trackMutation(() => mutations.updatePlayer(id, pos))
			),
		])
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	// Clean up move timer on unmount
	useEffect(() => {
		return () => {
			if (moveTimerRef.current) {
				clearTimeout(moveTimerRef.current)
			}
		}
	}, [])

	// Fetch playbook to get team_id for labels
	const { data: playbookData } = useQuery({
		queryKey: playbookKeys.detail(playbookId),
		queryFn: async () => {
			const response = await secureFetch(`/api/playbooks/${playbookId}`)
			if (!response.ok) throw new Error('Failed to fetch playbook')
			return response.json()
		},
		select: (data) => data.playbook,
		staleTime: Infinity, // Playbook data rarely changes
		enabled: !!playbookId,
	})

	const teamId = playbookData?.team_id ? String(playbookData.team_id) : null

	// Local UI state
	const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([])
	const [showPlayBar, setShowPlayBar] = useState(true)

	// Note: UnifiedSearchBar now manages its own chip state via useComposeMode


	// Modals
	const [showRenameModal, setShowRenameModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [modalTargetPlayId, setModalTargetPlayId] = useState<string | null>(null)
	const [targetPlayName, setTargetPlayName] = useState('')
	// Removed unused unsaved changes dialog state (block system auto-saves)
	const [isAnimating, setIsAnimating] = useState(false)

	// Concept dialog state
	const [isConceptDialogOpen, setIsConceptDialogOpen] = useState(false)
	const [editingConceptId, setEditingConceptId] = useState<string | null>(null)

	// Concept data
	const {
		formations: _formations,
		createConcept,
		updateConcept,
		isLoading: _conceptsLoading,
	} = useConceptData(playbookId)

	// Playbook data
	const {
		plays: playbookPlays,
		createPlay: createPlaybookPlay,
		deletePlay: deletePlaybookPlay,
		updatePlay: updatePlaybookPlay,
		duplicatePlay: duplicatePlaybookPlay,
	} = usePlaybookData(playbookId)

	// Undo/redo handlers for keyboard shortcuts
	const handleUndo = useCallback(async () => {
		await undo()
		refetch()
	}, [undo, refetch])

	const handleRedo = useCallback(async () => {
		await redo()
		refetch()
	}, [redo, refetch])

	// Offset for duplicated objects (in feet)
	const DUPLICATION_OFFSET = { x: 2, y: 2 }

	const handleDuplicateSelection = useCallback(async () => {
		if (selectedObjectIds.length === 0) return

		// Wrap entire duplication in a single undo transaction
		const txId = startTransaction('Duplicate Selection')
		const newIds: string[] = []

		try {
			for (const id of selectedObjectIds) {
				// Check if it's a player
				const player = players.find(p => p.id === id)
				if (player) {
					const newPlayerId = await mutations.addPlayer({
						...player,
						id: `player-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
						x: player.x + DUPLICATION_OFFSET.x,
						y: player.y + DUPLICATION_OFFSET.y,
					})
					if (newPlayerId) newIds.push(newPlayerId)
					continue
				}

				// Check if it's a drawing
				const drawing = drawings.find(d => d.id === id)
				if (drawing) {
					// Create offset copy of control points
					const offsetPoints: typeof drawing.points = {}
					for (const pointId of Object.keys(drawing.points)) {
						const point = drawing.points[pointId]
						if (!point) continue
						offsetPoints[pointId] = {
							...point,
							x: point.x + DUPLICATION_OFFSET.x,
							y: point.y + DUPLICATION_OFFSET.y,
						}
					}

					await mutations.addDrawing({
						...drawing,
						id: `drawing-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
						points: offsetPoints,
					})
				}
			}
		} finally {
			endTransaction(txId)
		}

		// Select the newly created objects
		if (newIds.length > 0) {
			setSelectedObjectIds(newIds)
			refetch()
		}
	}, [selectedObjectIds, players, drawings, mutations, refetch, startTransaction, endTransaction])

	// Keyboard shortcuts - pass undo/redo and duplicate directly instead of using event bus
	useKeyboardShortcuts({
		setDrawingState: (updates) => setDrawingState(updates),
		currentTool: drawingState.tool,
		onUndo: handleUndo,
		onRedo: handleRedo,
		onDuplicate: handleDuplicateSelection,
	})

	// Clear selection when switching away from select tool
	useEffect(() => {
		if (drawingState.tool !== 'select' && selectedObjectIds.length > 0) {
			setSelectedObjectIds([])
		}
	}, [drawingState.tool, selectedObjectIds.length])

	// Guard for deletion
	const canDelete = useCallback((): boolean => {
		if (drawingState.tool !== 'select') return false
		return selectedObjectIds.length !== 0;

	}, [drawingState.tool, selectedObjectIds.length])

	// Selection change handler
	const handleSelectionChange = useCallback((ids: string[]) => {
		setSelectedObjectIds(ids)
	}, [])

	// Delete selected objects
	const deleteSelectedObjects = useCallback(async (objectIds: string[]) => {
		if (!canDelete()) return
		if (objectIds.length === 0) return

		// Wrap multi-delete in a single undo transaction
		const txId = startTransaction('Delete Selection')
		try {
			for (const id of objectIds) {
				const isPlayer = players.some(p => p.id === id)
				const isDrawing = drawings.some(d => d.id === id)

				if (isPlayer) {
					await mutations.deletePlayer(id)
				} else if (isDrawing) {
					await mutations.deleteDrawing(id)
				}
			}
		} finally {
			endTransaction(txId)
		}
		setSelectedObjectIds([])
		refetch()
	}, [canDelete, players, drawings, mutations, refetch, startTransaction, endTransaction])

	// Event bus handlers
	useEffect(() => {
		function handleAddComponent() {
			setIsConceptDialogOpen(true)
		}

		eventBus.on('component:add', handleAddComponent)
		return () => eventBus.off('component:add', handleAddComponent)
	}, [])


	// Handle delete selection event
	useEffect(() => {
		function handleDeleteSelection() {
			if (!canDelete()) return
			deleteSelectedObjects(selectedObjectIds)
		}

		eventBus.on('selection:delete', handleDeleteSelection)
		return () => eventBus.off('selection:delete', handleDeleteSelection)
	}, [selectedObjectIds, deleteSelectedObjects, canDelete])

	// Navigation handlers
	function handleBackToPlaybook() {
		// In block system, changes are auto-saved, so no dirty check needed
		navigateBackToPlaybook()
	}

	function navigateBackToPlaybook() {
		navigate(`/playbooks/${playbookId}?tab=plays`)
	}

	// Play management handlers
	async function handleAddPlay() {
		try {
			const newPlay = await createPlaybookPlay('Untitled Play')
			navigate(`/playbooks/${playbookId}/play/${newPlay.id}`)
		} catch (error) {
			console.error('Failed to create play:', error)
		}
	}

	function handleRenamePlay(playId: string) {
		const play = playbookPlays.find((p) => p.id === playId)
		if (!play) return

		setModalTargetPlayId(playId)
		setTargetPlayName(play.name)
		setShowRenameModal(true)
	}

	async function confirmRename() {
		if (!modalTargetPlayId || !targetPlayName.trim()) return

		try {
			await updatePlaybookPlay(modalTargetPlayId, { name: targetPlayName.trim() })
			setShowRenameModal(false)
			setModalTargetPlayId(null)
			setTargetPlayName('')
		} catch (error) {
			console.error('Failed to rename play:', error)
		}
	}

	function handleDeletePlayFromBar(playId: string) {
		setModalTargetPlayId(playId)
		setShowDeleteModal(true)
	}

	async function confirmDeleteFromBar() {
		if (!modalTargetPlayId) return

		const playIdToDelete = modalTargetPlayId
		setShowDeleteModal(false)
		setModalTargetPlayId(null)

		try {
			await deletePlaybookPlay(playIdToDelete)

			// If deleting current play, navigate away
			if (playIdToDelete === playBlockId) {
				const remainingPlays = playbookPlays.filter(p => p.id !== playIdToDelete)
				const nextPlay = remainingPlays[0]
				if (nextPlay) {
					navigate(`/playbooks/${playbookId}/play/${nextPlay.id}`)
				} else {
					navigate(`/playbooks/${playbookId}`)
				}
			}
		} catch (error) {
			console.error('Failed to delete play:', error)
		}
	}

	async function handleDuplicatePlay(playIdToDuplicate: string) {
		try {
			await duplicatePlaybookPlay(playIdToDuplicate)
		} catch (error) {
			console.error('Failed to duplicate play:', error)
		}
	}

	function handleStartAnimation() {
		setIsAnimating(true)
	}

	function handleStopAnimation() {
		setIsAnimating(false)
	}

	// Formation removal
	async function handleRemoveFormation() {
		if (!appliedFormation) return
		try {
			await mutations.removeFormation()
			queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		} catch (error) {
			console.error('Failed to remove formation:', error)
		}
	}

	// Concept removal (from AppliedChips - removes from play)
	async function handleRemoveConcept(referenceId: string) {
		await mutations.removeConceptReference(referenceId)
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}

	// Vocabulary selection handler for UnifiedSearchBar
	async function handleVocabularySelect(entry: VocabularyEntry) {
		console.log(`[handleVocabularySelect] entry:`, { entity_type: entry.entity_type, entity_id: entry.entity_id, code: entry.code, playBlockId })
		try {
			const result = await composerRef.current.applyEntry(
				entry,
				mutations,
				players,
				drawings,
			)

			// Log any warnings for debugging
			if (result.warnings.length > 0) {
				console.warn('Composition warnings:', result.warnings)
			}
			if (result.errors.length > 0) {
				console.error('Composition errors:', result.errors)
			}

			// Invalidate cache to refetch the resolved play
			queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		} catch (error) {
			console.error('Failed to apply vocabulary entry:', error)
		}
	}

	// Navigate to a play selected from the search dropdown
	function handlePlaySelect(playId: string) {
		navigate(`/playbooks/${playbookId}/play/${playId}`)
	}

	// Create a new play from compose mode chips
	async function handleCreatePlayFromCompose(chips: PlayCallChip[], name: string) {
		try {
			const newPlay = await createPlaybookPlay(name)
			// TODO Phase 7: auto-compose tokens after navigation
			// For now, create the play with the name and navigate.
			// The coach can then apply vocabulary entries from the search bar.
			navigate(`/playbooks/${playbookId}/play/${newPlay.id}`)
		} catch (error) {
			console.error('Failed to create play:', error)
		}
	}

	// Concept dialog handlers
	function handleCloseConceptDialog() {
		setIsConceptDialogOpen(false)
		setEditingConceptId(null)
		setDrawingState({ tool: 'select' })
	}

	async function handleSaveConcept(conceptData: {
		name: string
		description?: string
		targeting_mode: 'absolute_role' | 'relative_selector'
		ball_position?: 'left' | 'center' | 'right'
		play_direction?: 'left' | 'right' | 'na'
	}) {
		if (editingConceptId) {
			await updateConcept(editingConceptId, conceptData)
		} else {
			await createConcept({
				...conceptData,
				assignments: [],
			})
		}
	}

	function handleSaveSelectionAsConcept() {
		setIsConceptDialogOpen(true)
	}

	function handleDeleteSelection() {
		deleteSelectedObjects(selectedObjectIds)
	}

	// Player mutation handlers (debounced position updates)
	const handlePlayerMove = useCallback((id: string, x: number, y: number) => {
		// Find previous position: check pending moves first, then current players array
		const pendingPos = pendingMovesRef.current.get(id)
		const currentPlayer = playersRef.current.find(p => p.id === id)
		const prevX = pendingPos?.x ?? currentPlayer?.x ?? x
		const prevY = pendingPos?.y ?? currentPlayer?.y ?? y
		const dx = x - prevX
		const dy = y - prevY

		pendingMovesRef.current.set(id, { x, y })

		// Move linked drawings with the player
		if (dx !== 0 || dy !== 0) {
			for (const drawing of drawingsRef.current) {
				if (drawing.playerId !== id) continue

				// Shift all control points — use pending position if available to avoid stale reads
				for (const [pointId, point] of Object.entries(drawing.points)) {
					const pendingPoint = pendingControlPointMovesRef.current.get(pointId)
					const baseX = pendingPoint?.x ?? point.x
					const baseY = pendingPoint?.y ?? point.y
					pendingControlPointMovesRef.current.set(pointId, {
						drawingId: drawing.id,
						x: baseX + dx,
						y: baseY + dy,
					})
				}
			}
		}

		if (moveTimerRef.current) {
			clearTimeout(moveTimerRef.current)
		}
		moveTimerRef.current = setTimeout(flushPendingMoves, POSITION_DEBOUNCE_MS)
	}, [flushPendingMoves])

	const handlePlayerAdd = useCallback(async (player: Omit<Player, 'id'>): Promise<string> => {
		const serverId = await trackMutation(() => mutations.addPlayer({
			id: `player-${Date.now()}`,
			...player,
		} as Player))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		return serverId
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handlePlayerDelete = useCallback(async (id: string) => {
		await trackMutation(() => mutations.deletePlayer(id))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handlePlayerLabelChange = useCallback(async (id: string, label: string) => {
		try {
			await trackMutation(() => mutations.updatePlayer(id, { label }))
			queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		} catch (error) {
			console.error('Failed to update player label:', error)
		}
	}, [mutations, trackMutation, queryClient, playBlockId])

	// Drawing mutation handlers
	const handleDrawingAdd = useCallback(async (drawing: Omit<Drawing, 'id'>) => {
		await trackMutation(() => mutations.addDrawing({
			id: `drawing-${Date.now()}`,
			...drawing,
		} as Drawing))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handleDrawingUpdate = useCallback(async (id: string, updates: Partial<Drawing>) => {
		await trackMutation(() => mutations.updateDrawing(id, updates))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handleControlPointUpdate = useCallback(async (drawingId: string, pointId: string, x: number, y: number) => {
		await trackMutation(() => mutations.updateControlPoint(drawingId, pointId, x, y))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handleDrawingDelete = useCallback(async (id: string) => {
		await trackMutation(() => mutations.deleteDrawing(id))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	// Drawing-player link handlers
	const handleLinkDrawingToPlayer = useCallback(async (drawingId: string, pointId: string, playerId: string) => {
		await trackMutation(() => mutations.linkDrawingToPlayer(drawingId, pointId, playerId))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	const handleUnlinkDrawingFromPlayer = useCallback(async (drawingId: string) => {
		await trackMutation(() => mutations.unlinkDrawingFromPlayer(drawingId))
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [mutations, trackMutation, queryClient, playBlockId])

	// Loading state
	if (isLoading) {
		return <Loading text="Loading play..." fullscreen />
	}

	// Error state
	if (error) {
		return (
			<div className="page-error">
				<div className="page-error-content">
					<p className="page-error-message">
						Failed to load play: {error.message}
					</p>
				</div>
			</div>
		)
	}

	return (
		<>
		<EditorPageLayout
			as="main"
			toolbar={
				<Toolbar
					drawingState={drawingState}
					setDrawingState={(updates) => setDrawingState(updates)}
					hashAlignment={hashAlignment}
					setHashAlignment={setHashAlignment}
					showPlayBar={showPlayBar}
					setShowPlayBar={setShowPlayBar}
					playId={playBlockId}
					onAnimate={() => isAnimating ? handleStopAnimation() : handleStartAnimation()}
					isAnimating={isAnimating}
					onUndo={handleUndo}
					onRedo={handleRedo}
					canUndo={canUndo}
					canRedo={canRedo}
					players={players}
					saveStatus={saveStatus}
					onRevertSession={revertToSnapshot}
					hasSnapshot={hasSnapshot}
				/>
			}
			header={
				<PlayHeader
					teamId={teamId}
					playbookId={playbookId}
					initialChips={currentCallChips}
					onChipsChange={handleChipsChange}
					onBackToPlaybook={handleBackToPlaybook}
					onVocabularySelect={handleVocabularySelect}
					onPlaySelect={handlePlaySelect}
					onCreatePlay={handleCreatePlayFromCompose}
				/>
			}
			canvasArea={
				<div className="play-editor-canvas-area">
					<AppliedChips
						formationBlock={appliedFormation}
						conceptReferences={appliedConcepts}
						onRemoveFormation={handleRemoveFormation}
						onRemoveConcept={handleRemoveConcept}
					/>

					<ErrorBoundary
						fallback={
							<div className="page-error">
								<div className="page-error-content">
									<p className="page-error-message">
										The play canvas encountered an error.
									</p>
									<button
										type="button"
										onClick={() => window.location.reload()}
										className="play-editor-dialog-confirm"
									>
										Reload page
									</button>
								</div>
							</div>
						}
					>
						<CanvasViewportProvider>
							<BlockAwarePlayCanvas
								players={players}
								drawings={drawings}
								drawingState={drawingState}
								selectedObjectIds={selectedObjectIds}
								onSelectionChange={handleSelectionChange}
								onPlayerMove={handlePlayerMove}
								onPlayerAdd={handlePlayerAdd}
								onPlayerDelete={handlePlayerDelete}
								onPlayerLabelChange={handlePlayerLabelChange}
								onDrawingAdd={handleDrawingAdd}
								onDrawingUpdate={handleDrawingUpdate}
								onDrawingDelete={handleDrawingDelete}
								onControlPointUpdate={handleControlPointUpdate}
								onLinkDrawingToPlayer={handleLinkDrawingToPlayer}
								onUnlinkDrawingFromPlayer={handleUnlinkDrawingFromPlayer}
								hashAlignment={hashAlignment}
								animationOverlay={isAnimating ? (
									<InPlaceAnimationOverlay
										playId={playBlockId}
										players={players}
										drawings={drawings}
										onStop={handleStopAnimation}
									/>
								) : undefined}
							/>
						</CanvasViewportProvider>
					</ErrorBoundary>
				</div>
			}
			sidePanel={
				<EditorCardsPanel
					items={playbookPlays}
					cardType="play"
					currentItemId={playBlockId}
					showPanel={showPlayBar}
					onOpen={(targetPlayId) => navigate(`/playbooks/${playbookId}/play/${targetPlayId}`)}
					onAdd={handleAddPlay}
					onRename={handleRenamePlay}
					onDelete={handleDeletePlayFromBar}
					onDuplicate={handleDuplicatePlay}
					onAnimate={() => setIsAnimating(true)}
				/>
			}
			overlay={
				<SelectionOverlay
					selectedCount={selectedObjectIds.length}
					onSaveAsConcept={handleSaveSelectionAsConcept}
					onDelete={handleDeleteSelection}
					onDuplicate={handleDuplicateSelection}
				/>
			}
		/>

			{/* Rename Play Dialog */}
			<Dialog
				open={showRenameModal}
				onOpenChange={(open) => {
					setShowRenameModal(open)
					if (!open) {
						setModalTargetPlayId(null)
						setTargetPlayName('')
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Rename Play</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<label className="play-editor-dialog-label">Play Name</label>
							<Input
								type="text"
								value={targetPlayName}
								onChange={(e) => setTargetPlayName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										confirmRename()
									}
								}}
								placeholder="Enter play name..."
								autoFocus
							/>
						</div>
						<div className="play-editor-dialog-actions">
							<button
								onClick={() => {
									setShowRenameModal(false)
									setModalTargetPlayId(null)
									setTargetPlayName('')
								}}
								className="play-editor-dialog-cancel"
							>
								Cancel
							</button>
							<button
								onClick={confirmRename}
								disabled={!targetPlayName.trim()}
								className="play-editor-dialog-confirm"
							>
								Rename
							</button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Delete Play Confirmation Dialog */}
			<Dialog
				open={showDeleteModal}
				onOpenChange={(open) => {
					setShowDeleteModal(open)
					if (!open) setModalTargetPlayId(null)
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Play</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<p className="play-editor-dialog-message">
							Are you sure you want to delete this play?
							This action cannot be undone.
						</p>
						<div className="play-editor-dialog-actions">
							<button
								onClick={() => {
									setShowDeleteModal(false)
									setModalTargetPlayId(null)
								}}
								className="play-editor-dialog-cancel"
							>
								Cancel
							</button>
							<button
								onClick={confirmDeleteFromBar}
								className="play-editor-dialog-delete"
							>
								Delete
							</button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Concept Dialog */}
			<ConceptDialog
				isOpen={isConceptDialogOpen}
				onClose={handleCloseConceptDialog}
				mode={editingConceptId ? DIALOG_MODE_EDIT : DIALOG_MODE_CREATE}
				concept={undefined}
				teamId={teamId ?? ''}
				playbookId={playbookId}
				onSave={handleSaveConcept as (_concept: Partial<Concept>) => Promise<void>}
			/>

		</>
	)
}
